import React, { useState, useEffect, useRef } from "react";
import "../MyAuctions/MyAuctions.css";
import { Link, useNavigate } from "react-router-dom";
import {
  Calendar,
  Clock,
  Users,
  Eye,
  Play,
  Search,
  RefreshCw,
  Plus,
  Gavel,
  Download,
  Pencil,
} from "lucide-react";
import apiService from "../../../services/apiAuctionService";
import { BaseAuction } from "../../../types/auction";
import { useAuth } from "../../../contexts/AuthContext";
import { API_BASE_URL } from "../../../services/apiConfig";
import { generateAuctionReport } from "./AuctionReportGenerator";

const MyCreatedA: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [auctions, setAuctions] = useState<BaseAuction[]>([]);
  const [loading, setLoading] = useState(true);
  const [apiError, setApiError] = useState<string | null>(null);
  const [isFetching, setIsFetching] = useState(false);
  const [participantCounts, setParticipantCounts] = useState<{
    [key: string]: number;
  }>({});
  const [now, setNow] = useState<number>(Date.now());
  
  // Download modal states
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);
  const [selectedAuction, setSelectedAuction] = useState<BaseAuction | null>(null);

  const firstLoadRef = useRef(true);

  // Lock/unlock body scroll when modal opens/closes
  useEffect(() => {
    if (showDownloadModal) {
      // Lock body scroll and add class
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = '0px';
      document.body.classList.add('modal-open');
      
      if (isDownloading) {
        document.body.classList.add('generating-pdf');
      }
    } else {
      // Unlock body scroll and remove classes
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('modal-open');
      document.body.classList.remove('generating-pdf');
    }

    // Cleanup on unmount
    return () => {
      document.body.style.overflow = '';
      document.body.style.paddingRight = '';
      document.body.classList.remove('modal-open');
      document.body.classList.remove('generating-pdf');
    };
  }, [showDownloadModal, isDownloading]);

  // Helper: Check if user created the auction
  const isCreatedByUser = (auction: BaseAuction) => {
    if (!user) return false;
    return (
      auction.createdBy === user.id ||
      auction.userId === user.id ||
      auction.createdBy === user.companyName ||
      auction.auctioneerCompany === user.companyName ||
      (auction as any).auctioneerPhone === user.phoneNumber
    );
  };

  // Helper: Format auction number with proper padding (AUC001, AUC002, etc.)
  const formatAuctionNumber = (auctionNo: string): string => {
    // Extract number from formats like "AUC-1", "AUC1", or just "1"
    const match = auctionNo.match(/\d+/);
    if (match) {
      const num = parseInt(match[0], 10);
      return `AUC${String(num).padStart(3, "0")}`;
    }
    return auctionNo; // Return original if no number found
  };

  // Helper: Fetch participants by auction ID
  const fetchParticipantsByAuctionId = async (auctionId: string | number) => {
    try {
      const token =
        localStorage.getItem("token") || localStorage.getItem("authToken");
      if (!token) return null;

      const res = await fetch(
        `${API_BASE_URL}/auction/${auctionId}/participants`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!res.ok) return null;
      return await res.json();
    } catch (err) {
      console.error(
        `Error fetching participants for auction ${auctionId}:`,
        err
      );
      return null;
    }
  };

  // Fetch participant counts for all auctions
  const fetchParticipantCounts = async (auctions: BaseAuction[]) => {
    const counts: { [key: string]: number } = {};
    // Requests within a batch already run concurrently; a larger batch plus
    // dropping the fixed inter-batch pause below cuts this from several
    // seconds down to roughly one round trip's worth of time for a typical
    // list, without changing what each request does or how counts are used.
    const batchSize = 8;

    for (let i = 0; i < auctions.length; i += batchSize) {
      const batch = auctions.slice(i, i + batchSize);

      await Promise.all(
        batch.map(async (auction) => {
          const auctionId = auction.backendId || auction.id;
          if (auctionId) {
            try {
              const participantsData = await fetchParticipantsByAuctionId(
                auctionId
              );
              if (
                participantsData?.participants &&
                Array.isArray(participantsData.participants)
              ) {
                const validParticipants = participantsData.participants.filter(
                  (p: any) =>
                    p && (typeof p === "string" ? p.trim() !== "" : true)
                );
                counts[auctionId] = validParticipants.length;
              } else {
                counts[auctionId] = auction.participants?.length || 0;
              }
            } catch (error) {
              counts[auctionId] = auction.participants?.length || 0;
            }
          }
        })
      );
    }

    setParticipantCounts(counts);
  };

  const getParticipantCount = (auction: BaseAuction): number => {
    const auctionId = auction.backendId || auction.id;
    const apiCount = participantCounts[auctionId];
    return apiCount !== undefined
      ? apiCount
      : auction.participants?.length ?? 0;
  };

  // Debounce search
  useEffect(() => {
    const id = setTimeout(() => setDebouncedSearch(searchTerm), 350);
    return () => clearTimeout(id);
  }, [searchTerm]);

  // Fetch auctions
  const fetchAuctions = async (signal?: AbortSignal) => {
    try {
      let data: BaseAuction[] = [];


      if (!debouncedSearch) {
        try {
          data = await apiService.fetchMyAuctions(
            statusFilter === "all" ? undefined : statusFilter,
            signal
          );
        } catch (e) {
          console.warn(
            "[MyCreatedAuctions] fetchMyAuctions failed, falling back",
            e
          );
        }
      }

      if (data.length === 0) {
        const params = {
          status: statusFilter,
          type: "created",
          search: debouncedSearch,
          signal,
        };
        data = await apiService.fetchFilteredAuctions(params);

        if (data.length > 0 && user?.id) {
          data = data.filter(isCreatedByUser);
        }
      }

      const finalCreatedCheck = data.filter(isCreatedByUser);
      if (finalCreatedCheck.length !== data.length) {
        data = finalCreatedCheck;
      }

      setAuctions(data);

      if (data.length > 0) {
        await fetchParticipantCounts(data);
      }
    } catch (err: any) {
      // Ignore abort errors - they're expected when requests are cancelled
      if (err?.name === 'AbortError') return;
      
      console.error("[MyCreatedAuctions] fetchAuctions failed:", err);
      setApiError(err?.message || "Failed to load auctions");
      setAuctions([]);
    }
  };

  useEffect(() => {
    if (!user) return;
    setApiError(null);
    const controller = new AbortController();
    const immediate = firstLoadRef.current;
    if (immediate) setLoading(true);
    else setIsFetching(true);

    fetchAuctions(controller.signal).finally(() => {
      if (!controller.signal.aborted) {
        if (immediate) {
          setLoading(false);
          firstLoadRef.current = false;
        } else {
          setIsFetching(false);
        }
      }
    });

    return () => controller.abort();
  }, [statusFilter, debouncedSearch, user]);

  const handleManualRefresh = async () => {
    setIsFetching(true);
    try {
      await fetchAuctions();
    } finally {
      setIsFetching(false);
    }
  };

  // Date/Time helpers
  const getAuctionStart = (auction: BaseAuction) => {
    try {
      const iso = `${auction.auctionDate}T${auction.auctionStartTime}:00`;
      return new Date(iso);
    } catch {
      return new Date();
    }
  };

  const formatAuctionDate = (dateStr: string) => {
    const [y, m, d] = dateStr.split("-");
    return `${d}-${m}-${y}`;
  };

  const formatAuctionTime = (time24: string) => {
    const [h, minute] = time24.split(":").map(Number);
    const suffix = h >= 12 ? "PM" : "AM";
    const hour12 = h % 12 || 12;
    return `${hour12}:${String(minute).padStart(2, "0")} ${suffix}`;
  };

  const getAuctionEnd = (auction: BaseAuction) => {
    const start = getAuctionStart(auction).getTime();
    let durationInMinutes = auction.duration || 60;
    if (durationInMinutes > 1440) {
      durationInMinutes = Math.floor(durationInMinutes / 60);
    }
    const durMs = durationInMinutes * 60 * 1000;
    return new Date(start + durMs);
  };

  const getDerivedStatus = (
    auction: BaseAuction,
    nowMs: number
  ): BaseAuction["status"] => {
    if (auction.status === "completed") return "completed";
    const start = getAuctionStart(auction).getTime();
    const end = getAuctionEnd(auction).getTime();
    if (nowMs < start) return "upcoming";
    if (nowMs >= end) return "completed";
    return "live";
  };

  // Timer for status monitoring
  useEffect(() => {
    const id = setInterval(() => {
      const currentTime = Date.now();
      setNow(currentTime);

      const shouldRefresh = auctions.some((auction) => {
        const start = getAuctionStart(auction).getTime();
        const end = getAuctionEnd(auction).getTime();
        const currentStatus = auction.status;
        const derivedStatus = getDerivedStatus(auction, currentTime);

        return (
          (currentStatus === "upcoming" &&
            derivedStatus === "live" &&
            currentTime >= start + 30000) ||
          (currentStatus === "live" &&
            derivedStatus === "completed" &&
            currentTime >= end + 60000)
        );
      });

      if (shouldRefresh) {
        fetchAuctions();
      }
    }, 1000);

    return () => clearInterval(id);
  }, [auctions]);

  // Filter auctions
  const filterAuctions = (auctions: BaseAuction[]) => {
    const term = searchTerm.trim().toLowerCase();
    const nowMs = now;
    return auctions
      .filter((auction) => {
        const derivedStatus = getDerivedStatus(auction, nowMs);
        const matchesSearch =
          term === "" ||
          auction.title.toLowerCase().includes(term) ||
          auction.auctionNo.toLowerCase().includes(term) ||
          (auction.auctioneerCompany?.toLowerCase().includes(term) ?? false);
        const matchesStatus =
          statusFilter === "all" || derivedStatus === statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort(
        (a, b) => getAuctionStart(b).getTime() - getAuctionStart(a).getTime()
      );
  };

  const filteredAuctions = filterAuctions(auctions);

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "live":
        return <Play className="w-4 h-4" />;
      case "upcoming":
        return <Clock className="w-4 h-4" />;
      case "completed":
        return <Calendar className="w-4 h-4" />;
      default:
        return <Calendar className="w-4 h-4" />;
    }
  };

  const formatCountdown = (ms: number) => {
    if (ms <= 0) return "00d 00h 00m 00s";
    const totalSeconds = Math.floor(ms / 1000);
    const days = Math.floor(totalSeconds / (24 * 3600));
    const hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${String(days).padStart(2, "0")}d ${String(hours).padStart(
      2,
      "0"
    )}h ${String(minutes).padStart(2, "0")}m ${String(seconds).padStart(
      2,
      "0"
    )}s`;
  };

  const remainingToStart = (auction: BaseAuction) =>
    getAuctionStart(auction).getTime() - now;
  const remainingToEnd = (auction: BaseAuction) =>
    getAuctionEnd(auction).getTime() - now;

  // Show download confirmation modal
  const handleDownloadClick = (auction: BaseAuction) => {
    
    // Ensure clean state before opening
    setIsDownloading(false);
    setSelectedAuction(auction);
    
    // Small delay to ensure state is set before showing modal
    setTimeout(() => {
      setShowDownloadModal(true);
    }, 50);
  };

  // Close modal
  const handleCloseModal = () => {
    
    if (!isDownloading) {
      setShowDownloadModal(false);
      setTimeout(() => {
        setSelectedAuction(null);
      }, 300); // Clear after animation
    }
  };

  // Confirm and start download
  const handleConfirmDownload = async () => {
    
    if (!selectedAuction) {
      console.error('No auction selected');
      return;
    }
    
    setIsDownloading(true);
    
    try {
      
      // Add small delay to show loading state
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Generate the PDF
      await generateAuctionReport(selectedAuction);
      
      
      // Success - show success state briefly then close
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      
      setIsDownloading(false);
      setShowDownloadModal(false);
      
      // Clear selected auction after modal closes
      setTimeout(() => {
        setSelectedAuction(null);
      }, 300);
      
    } catch (error) {
      console.error("Failed to generate PDF:", error);
      setIsDownloading(false);
      alert("Could not create PDF report. Please try again.");
    }
  };

  // The skeleton below is rendered inline, further down, inside the same
  // header/filters chrome the loaded page uses (and in the same
  // .ap-myauctions-grid, at the same card width) instead of replacing the
  // whole page with a separate, differently-sized placeholder -- so nothing
  // jumps or resizes once the real auctions arrive.

  return (
    <div className="ap-myauctions-wrapper">
      {/* Header */}
      <div className="ap-myauctions-header">
        <div className="ap-myauctions-header-content">
          <div className="ap-myauctions-title-section">
            <h1 className="ap-myauctions-title">
              <Gavel className="w-8 h-8" />
              My Created Auctions
            </h1>
            <p className="ap-myauctions-subtitle">
              Manage your created auctions
            </p>
          </div>
          <Link
            to="/dashboard/new-auction"
            className="ap-myauctions-create-btn"
          >
            <Plus className="w-4 h-4" />
            Create Auction
          </Link>
        </div>
      </div>

      {/* Filters */}
      <div className="ap-myauctions-filters">
        <div className="ap-myauctions-filters-content">
          <div className="ap-myauctions-search-container">
            <div className="ap-myauctions-search-input-wrapper">
              <Search className="ap-myauctions-search-icon" />
              <input
                type="text"
                placeholder="Search auctions..."
                className="ap-myauctions-search-input"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>
          <div className="ap-myauctions-filter-controls">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="ap-myauctions-filter-select"
            >
              <option value="all">All Status</option>
              <option value="upcoming">Upcoming</option>
              <option value="live">Live</option>
              <option value="completed">Completed</option>
            </select>
            <button
              className={`ap-myauctions-filter-btn ${
                isFetching ? "opacity-75" : ""
              }`}
              onClick={handleManualRefresh}
              disabled={isFetching}
            >
              <RefreshCw
                className={`w-4 h-4 ${isFetching ? "animate-spin" : ""}`}
              />
              <span className="ml-1">
                {isFetching ? "Refreshing" : "Refresh"}
              </span>
            </button>
          </div>
        </div>
        {apiError && (
          <div className="ap-myauctions-error-banner">
            <span>{apiError}</span>
            <button onClick={() => fetchAuctions()}>Retry</button>
          </div>
        )}
      </div>

      {/* Auctions List */}
      <div className="ap-myauctions-content">
        {isFetching && !loading && (
          <div className="ap-myauctions-loading-inline">
            Loading latest auctions...
          </div>
        )}
        {loading ? (
          <div className="ap-myauctions-grid">
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} className="ap-myauctions-card ap-skel-card">
                <div className="ap-myauctions-card-header">
                  <div className="ap-myauctions-card-title-section">
                    <div className="ap-skel-line ap-skel-title" style={{ width: "65%" }} />
                    <div className="ap-skel-badge" />
                  </div>
                  <div className="ap-skel-line short" style={{ height: 10 }} />
                </div>
                <div className="ap-myauctions-card-info">
                  <div className="ap-myauctions-info-item">
                    <div className="ap-skel-line" style={{ width: "55%", height: 10 }} />
                    <div className="ap-skel-line" style={{ width: "80%" }} />
                  </div>
                  <div className="ap-myauctions-info-item">
                    <div className="ap-skel-line" style={{ width: "55%", height: 10 }} />
                    <div className="ap-skel-line" style={{ width: "40%" }} />
                  </div>
                </div>
                <div className="ap-myauctions-card-actions">
                  <div className="ap-skel-line" style={{ width: "100%", height: 38, borderRadius: 8, margin: 0 }} />
                </div>
              </div>
            ))}
          </div>
        ) : filteredAuctions.length > 0 ? (
          <div className="ap-myauctions-grid">
            {filteredAuctions.map((auction) => {
              const derivedStatus = getDerivedStatus(auction, now);
              const startsInMs = remainingToStart(auction);
              const endsInMs = remainingToEnd(auction);

              return (
                <div key={auction.id} className="ap-myauctions-card">
                  <div className="ap-myauctions-card-header">
                    <div className="ap-myauctions-card-title-section">
                      <h3 className="ap-myauctions-card-title">
                        {auction.title}
                      </h3>
                      <div className="flex items-center gap-2">
                        <span
                          className={`ap-myauctions-status-badge ap-myauctions-status-${derivedStatus}`}
                        >
                          {getStatusIcon(derivedStatus)}
                          {derivedStatus.toUpperCase()}
                        </span>

                      </div>
                    </div>
                    <p className="ap-myauctions-card-subtitle">
                      Auction No: {formatAuctionNumber(auction.auctionNo)}
                    </p>
                  </div>
                  <div className="ap-myauctions-card-info">
                    <div className="ap-myauctions-info-item">
                      <span className="ap-myauctions-info-label">
                        Date & Time:
                      </span>
                      <p className="ap-myauctions-info-value">
                        <Calendar className="w-4 h-4" />
                        {formatAuctionDate(auction.auctionDate)} at{" "}
                        {formatAuctionTime(auction.auctionStartTime)}
                      </p>
                    </div>
                    <div className="ap-myauctions-info-item">
                      <span className="ap-myauctions-info-label">
                        Participants:
                      </span>
                      <p className="ap-myauctions-info-value">
                        <Users className="w-4 h-4" />
                        {getParticipantCount(auction)}
                      </p>
                    </div>
                  </div>
                  <div className="ap-myauctions-card-meta">
                    {derivedStatus === "upcoming" && (
                      <div className="ap-myauctions-countdown">
                        Starts in: {formatCountdown(startsInMs)}
                      </div>
                    )}
                    {derivedStatus === "live" && (
                      <div className="ap-myauctions-countdown">
                        Live • {formatCountdown(Math.max(endsInMs, 0))}
                      </div>
                    )}
                  </div>
                  <div className="ap-myauctions-card-actions">
                    <Link
                      to={`/dashboard/my-auction/${
                        auction.backendId || auction.id
                      }`}
                      className="ap-myauctions-action-btn ap-myauctions-view-btn"
                    >
                      <Eye className="w-4 h-4" />
                      View Details
                    </Link>
                    {/* Editable only until the auction goes live */}
                    {derivedStatus === "upcoming" && (
                      <Link
                        to={`/dashboard/edit-auction/${
                          auction.backendId || auction.id
                        }`}
                        className="ap-myauctions-action-btn ap-myauctions-view-btn"
                      >
                        <Pencil className="w-4 h-4" />
                        Edit
                      </Link>
                    )}
                    {derivedStatus === "live" && (
                      <Link
                        to={`/dashboard/auctioneer-live/${
                          auction.backendId || auction.id
                        }`}
                        className="ap-myauctions-action-btn ap-myauctions-live-btn"
                      >
                        <Gavel className="w-4 h-4" />
                        Join as Auctioneer
                      </Link>
                    )}
                    {derivedStatus === "completed" && (
                      <button
                        onClick={() => handleDownloadClick(auction)}
                        className="ap-myauctions-action-btn ap-myauctions-download-btn"
                      >
                        <Download className="w-4 h-4" />
                        Download Report
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="ap-myauctions-empty">
            <div className="ap-myauctions-empty-content">
              <Gavel className="ap-myauctions-empty-icon" />
              <h3 className="ap-myauctions-empty-title">No auctions found</h3>
              <p className="ap-myauctions-empty-subtitle">
                You haven't created any auctions yet. Start by creating your
                first auction!
              </p>
              <Link
                to="/dashboard/new-auction"
                className="ap-myauctions-empty-action"
              >
                <Plus className="w-4 h-4" />
                Create First Auction
              </Link>
            </div>
          </div>
        )}
      </div>

      {/* Download Confirmation Modal */}
      {showDownloadModal && selectedAuction && (
        <div 
          className="modal-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: isDownloading ? 'rgba(0, 0, 0, 0.75)' : 'rgba(0, 0, 0, 0.6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 99999,
            backdropFilter: 'blur(6px)',
            WebkitBackdropFilter: 'blur(6px)',
            animation: 'fadeIn 0.3s ease-out forwards',
            isolation: 'isolate',
          }}
          onClick={handleCloseModal}
        >
          <div 
            className="modal-content"
            style={{
              background: 'var(--card-background)',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.3), 0 10px 10px -5px rgba(0, 0, 0, 0.2)',
              position: 'relative',
              animation: 'modalSlideIn 0.3s ease-out forwards',
              transform: 'translateY(0) scale(1)',
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            {!isDownloading && (
              <button
                onClick={handleCloseModal}
                style={{
                  position: 'absolute',
                  top: '1rem',
                  right: '1rem',
                  background: 'transparent',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: 'var(--text-secondary)',
                  lineHeight: 1,
                  padding: '0.5rem',
                  borderRadius: '50%',
                  transition: 'all 0.2s',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = 'var(--hover-surface)';
                  e.currentTarget.style.color = 'var(--text-primary)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                  e.currentTarget.style.color = 'var(--text-secondary)';
                }}
              >
                ×
              </button>
            )}

            <div style={{ textAlign: 'center' }}>
              {/* Icon */}
              <div 
                style={{
                  width: '80px',
                  height: '80px',
                  margin: '0 auto 1.5rem',
                  background: isDownloading 
                    ? 'linear-gradient(135deg, #ff6b00 0%, #e85d04 100%)'
                    : 'linear-gradient(135deg, #1261d6 0%, #0b3fa0 100%)',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: '0 10px 15px -3px rgba(18, 97, 214, 0.3)',
                }}
              >
                {isDownloading ? (
                  <RefreshCw 
                    style={{
                      width: '40px',
                      height: '40px',
                      color: 'white',
                      animation: 'spin 1s linear infinite',
                    }}
                  />
                ) : (
                  <Download 
                    style={{
                      width: '40px',
                      height: '40px',
                      color: 'white',
                    }}
                  />
                )}
              </div>

              {/* Title */}
              <h2 
                style={{
                  fontSize: '24px',
                  fontWeight: 'bold',
                  color: 'var(--text-primary)',
                  marginBottom: '0.75rem',
                }}
              >
                {isDownloading ? 'Generating Report...' : 'Download Auction Report?'}
              </h2>

              {/* Description */}
              <p 
                style={{
                  color: 'var(--text-secondary)',
                  marginBottom: '1.5rem',
                  lineHeight: '1.6',
                  fontSize: '15px',
                }}
              >
                {isDownloading ? (
                  <>
                    Please wait while we generate your PDF report.
                    <br />
                    <strong style={{ color: 'var(--primary-color)' }}>
                      Do not close this window...
                    </strong>
                  </>
                ) : (
                  <>
                    Download complete report for <strong>{selectedAuction?.title}</strong>
                    <br />
                    <span style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>
                      Auction No: {selectedAuction ? formatAuctionNumber(selectedAuction.auctionNo) : ''}
                    </span>
                  </>
                )}
              </p>

              {/* Progress indicator */}
              {isDownloading && (
                <>
                  <div 
                    style={{
                      width: '100%',
                      height: '6px',
                      background: 'var(--border-color)',
                      borderRadius: '3px',
                      overflow: 'hidden',
                      marginBottom: '1rem',
                    }}
                  >
                    <div 
                      style={{
                        height: '100%',
                        background: 'linear-gradient(90deg, #1261d6 0%, #ff6b00 50%, #1261d6 100%)',
                        backgroundSize: '200% 100%',
                        animation: 'progressBar 1.5s ease-in-out infinite',
                        borderRadius: '3px',
                      }}
                    />
                  </div>
                  <p style={{
                    fontSize: '12px',
                    color: 'var(--text-secondary)',
                    marginBottom: '0.5rem',
                    fontStyle: 'italic'
                  }}>
                    Preparing document for download...
                  </p>
                </>
              )}

              {/* Buttons */}
              {!isDownloading && (
                <div 
                  style={{
                    display: 'flex',
                    gap: '1rem',
                    justifyContent: 'center',
                    marginTop: '2rem',
                  }}
                >
                  <button
                    onClick={handleCloseModal}
                    style={{
                      padding: '0.75rem 1.5rem',
                      borderRadius: '8px',
                      border: '2px solid var(--border-color)',
                      background: 'var(--card-background)',
                      color: 'var(--text-secondary)',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '15px',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--hover-surface)';
                      e.currentTarget.style.borderColor = 'var(--border-color)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'var(--card-background)';
                      e.currentTarget.style.borderColor = 'var(--border-color)';
                    }}
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmDownload}
                    style={{
                      padding: '0.75rem 2rem',
                      borderRadius: '8px',
                      border: 'none',
                      background: 'linear-gradient(135deg, #1261d6 0%, #0b3fa0 100%)',
                      color: 'white',
                      fontWeight: '600',
                      cursor: 'pointer',
                      transition: 'all 0.2s',
                      fontSize: '15px',
                      boxShadow: '0 4px 6px -1px rgba(18, 97, 214, 0.3)',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = 'translateY(-2px)';
                      e.currentTarget.style.boxShadow = '0 10px 15px -3px rgba(18, 97, 214, 0.4)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = 'translateY(0)';
                      e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(18, 97, 214, 0.3)';
                    }}
                  >
                    <Download style={{ width: '16px', height: '16px', display: 'inline-block', marginRight: '8px', verticalAlign: 'middle' }} />
                    Download Report
                  </button>
                </div>
              )}
            </div>

            {/* Add keyframes for animations */}
            <style>{`
              @keyframes fadeIn {
                0% {
                  opacity: 0;
                }
                100% {
                  opacity: 1;
                }
              }
              
              @keyframes modalSlideIn {
                0% {
                  opacity: 0;
                  transform: translateY(-30px) scale(0.9);
                }
                100% {
                  opacity: 1;
                  transform: translateY(0) scale(1);
                }
              }
              
              @keyframes spin {
                0% { 
                  transform: rotate(0deg); 
                }
                100% { 
                  transform: rotate(360deg); 
                }
              }
              
              @keyframes progressBar {
                0% { 
                  background-position: 200% 0; 
                }
                100% { 
                  background-position: -200% 0; 
                }
              }
              
              /* Force styles to prevent blur and white screen issues */
              .modal-overlay {
                will-change: opacity;
                -webkit-backdrop-filter: blur(6px);
                contain: layout style paint;
                isolation: isolate;
              }
              
              .modal-content {
                will-change: transform, opacity;
                backface-visibility: hidden;
                -webkit-backface-visibility: hidden;
                contain: layout style paint;
              }
              
              /* Prevent any white flashing during PDF generation */
              body.generating-pdf {
                overflow: hidden !important;
              }
              
              /* Hide any temporary containers */
              body > div[style*="position: fixed"][style*="-10000px"] {
                display: none !important;
                visibility: hidden !important;
                opacity: 0 !important;
              }
            `}</style>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyCreatedA;


